import { useGetCommissionsQuery, useUpdateCommissionMutation } from "api/TenantSlug/Commission/hooks.ts";
import { tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { FILTER_TYPES } from "components/UI/Table/constants/filter.ts";
import { TableColumnType } from "components/UI/Table/types/table.ts";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { CountryCell } from "components/UI/Table/UI/CountryCell/CountryCell.tsx";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell.tsx";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell.tsx";
import { ComposedValue } from "components/UI/Table/UI/InteractiveCell/components/WithPayoutFormula.tsx";
import { InteractiveCell } from "components/UI/Table/UI/InteractiveCell/InteractiveCell.ts";
import { StatusCell } from "components/UI/Table/UI/StatusCell/StatusCell.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions.ts";
import { useUser } from "hooks/useUser.ts";
import { COMMISSIONS_PRIORITY_SORT } from "localConstants.ts";
import React, { FC, useEffect, useMemo } from "react";
import { useRecoilValue } from "recoil";
import { CommissionType, PermissionEnum } from "types/enums.ts";
import { DefaultStatus, TableId } from "types/models.ts";
import { updateChildCommission, updateParentCommission } from "utils/commission/updateCommission";

import { CommissionsTable } from "./CommissionsTable.tsx";
import { ShortenedIdWithCopy } from "../../UI/ShortenedIdWithCopy";
import { userGroup } from "../CommissionGroupTable/temp.ts";

import CommissionData = App.Data.CommissionData;
import CascadeData = App.Data.CascadeData;
import CountryData = App.Data.CountryData;

interface Props {
    offer?: App.Data.OfferData;
    cascade?: CascadeData;
    commissionGroupId?: string | null;
    editPermission?: PermissionEnum;
    showProvisionGroups?: boolean;
    commissionType: "Tenant" | "User";
}

export const CommissionsTableContainer: FC<Props> = ({
    offer,
    cascade,
    commissionGroupId,
    showProvisionGroups = true,
    editPermission,
    commissionType,
}) => {
    const { t } = usePaldockTranslation("tables.commissions-table");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tC } = usePaldockTranslation("common");
    const permissionsUtils = usePermission();
    const refetchFromChildRef = React.useRef<() => void>();
    const user = useUser();
    const { filter, page } = useRecoilValue(tableAtom<CommissionData>()(TableId.CommissionsTable));

    const [key, setKey] = React.useState("");

    const fetchCommissionListQuery = useGetCommissionsQuery("commissions", {
        page: { number: page?.number, size: page?.size },
        filter: {
            filter: {
                ...(offer ? { "offer.id": offer.id, is_enabled: true } : {}),
                ...(cascade ? { "offer.id": cascade.offer_id, cascade_id: cascade.id, is_enabled: true } : {}),
                ...(filter?.filter ?? {}),
                commission_type: commissionGroupId || offer || cascade ? CommissionType.User : CommissionType.Tenant,
                ...(commissionGroupId && {
                    commission_group_id: commissionGroupId === userGroup.id ? "" : commissionGroupId,
                }),
            },
            sort: filter?.sort ?? COMMISSIONS_PRIORITY_SORT,
            include:
                "offer,user,cascade,parent,parent.user,offer.country,children,children.commissionGroup,commissionGroup",
        },
    });

    const { mutateAsync: updateCommission } = useUpdateCommissionMutation();

    const updateChild = (value: ComposedValue, record: CommissionData, childId: string) => {
        return updateChildCommission(value, record, childId, updateCommission, {
            onSuccess: () => {
                fetchCommissionListQuery.refetch();
                refetchFromChildRef.current?.();
            },
            successMessage: t("messages.update_success"),
            errorMessage: t("messages.update_error"),
        });
    };

    const updateParent = (value: ComposedValue, record: CommissionData) => {
        return updateParentCommission(value, record, updateCommission, {
            onSuccess: () => {
                fetchCommissionListQuery.refetch();
                refetchFromChildRef.current?.();
            },
            successMessage: t("messages.update_success"),
            errorMessage: t("messages.update_error"),
        });
    };

    const firstCommission = fetchCommissionListQuery.data?.commissions[0];

    const columns = useMemo(() => {
        const staticColumns: TableColumnType<CommissionData>[] = [
            {
                title: tG("offer"),
                dataIndex: "offer.title",
                key: "offer.title",
                sorter: true,
                defaultSorter: true,
                render: (_: string, record: CommissionData) => <EntityLinkCell.Offer entity={record.offer} />,
                ellipsis: true,
                width: 150,
                filterable: true,
                filterType: FILTER_TYPES.string,
            },
            ...(commissionType === "Tenant"
                ? [
                      {
                          title: tG("advertiser"),
                          dataIndex: "user.nickname",
                          key: "user.nickname",
                          sorter: true,
                          render: (_: string, record: CommissionData) => (
                              <EntityLinkCell.Advertiser entity={record.user} />
                          ),
                          ellipsis: true,
                          width: 150,
                          filterable: true,
                          filterType: FILTER_TYPES.string,
                      },
                  ]
                : []),
            ...(commissionType === "User"
                ? [
                      {
                          title: tG("advertiser"),
                          dataIndex: "parent.user.nickname",
                          key: "parent.user.nickname",
                          sorter: true,
                          render: (_: string, record: CommissionData) =>
                              record.parent ? <EntityLinkCell.Advertiser entity={record.parent.user} /> : <EmptyCell />,
                          ellipsis: true,
                          width: 150,
                          filterable: true,
                          filterType: FILTER_TYPES.string,
                      },
                  ]
                : []),
            {
                title: <ColumnHeaderTitle columnTitle={tG("conversion")} />,
                dataIndex: "conversion_type",
                key: "conversion_type",
                sorter: true,
                render: (type: string) => <EmptyCell>{type}</EmptyCell>,
                ellipsis: true,
                width: 120,
                filterable: true,
                filterType: FILTER_TYPES.conversionStateTypeSelect,
            },
            {
                title: <ColumnHeaderTitle columnTitle={tG("transaction")} />,
                dataIndex: "autocreate_conversion_type",
                key: "autocreate_conversion_type",
                sorter: true,
                render: (_, data) => {
                    let conversionType;
                    if (!data.autocreate_conversion_type || data.autocreate_conversion_type == "") {
                        conversionType = "sale";
                    } else {
                        conversionType = data.autocreate_conversion_type;
                    }
                    return (
                        <>
                            {conversionType} {data.is_autoapprove ? " ⚡" : ""}
                        </>
                    );
                },
                ellipsis: true,
                width: 120,
                filterable: true,
                filterType: FILTER_TYPES.string,
            },
            {
                title: tG("channel"),
                dataIndex: "cascade.title",
                key: "cascade.title",
                sorter: true,
                render: (_, data) => <EntityLinkCell.Cascade entity={data.cascade} />,
                ellipsis: true,
                width: 150,
                filterable: true,
                filterType: FILTER_TYPES.string,
            },
            {
                title: tG("description"),
                dataIndex: "description",
                key: "description",
                sorter: true,
                render: (description: string) => <EmptyCell>{description}</EmptyCell>,
                ellipsis: true,
                width: 200,
                filterable: true,
                filterType: FILTER_TYPES.string,
            },
            {
                title: tG("source"),
                dataIndex: "conversion_source",
                key: "conversion_source",
                sorter: true,
                render: (source: string) => <EmptyCell>{source}</EmptyCell>,
                ellipsis: true,
                visible: false,
                width: 150,
                filterable: true,
                filterType: FILTER_TYPES.conversionStateSourceSelect,
            },
            {
                title: <ColumnHeaderTitle columnTitle={tG("country")} />,
                dataIndex: "offer.country",
                key: "offer.country",
                inputType: "text",
                render: (country: CountryData) => (country ? <CountryCell country={country} /> : <EmptyCell />),
                filterable: false,
                ellipsis: true,
                width: 120,
                visible: false,
            },
            ...(commissionGroupId
                ? [
                      {
                          title: tG("parent_id"),
                          dataIndex: "parent_id",
                          key: "parent_id",
                          sorter: true,
                          ellipsis: true,
                          render: (parent_id: string) =>
                              parent_id ? <ShortenedIdWithCopy id={parent_id} /> : <EmptyCell />,
                          width: 160,
                          visible: false,
                          filterable: true,
                          filterType: FILTER_TYPES.string,
                      },
                  ]
                : []),
            ...(!commissionGroupId
                ? [
                      {
                          title: tG("id"),
                          dataIndex: "id",
                          key: "id",
                          sorter: true,
                          ellipsis: true,
                          render: (id: string) => (id ? <ShortenedIdWithCopy id={id} /> : <EmptyCell />),
                          width: 160,
                          visible: false,
                          filterable: true,
                          filterType: FILTER_TYPES.string,
                      },
                  ]
                : []),
        ];

        if (user.isAdmin()) {
            staticColumns.push({
                title: tG("status"),
                dataIndex: "is_enabled",
                key: "is_enabled",
                inputType: "text",
                render: (status: boolean) => (
                    <StatusCell
                        status={status ? DefaultStatus.Success : DefaultStatus.Error}
                        customLabels={{
                            [DefaultStatus.Success]: tC("active"),
                            [DefaultStatus.Error]: tC("paused"),
                        }}
                    />
                ),
                filterable: true,
                filterType: FILTER_TYPES.select,
                filterOptions: [
                    { label: tC("active"), value: true },
                    { label: tC("paused"), value: false },
                ],
                sorter: true,
                ellipsis: true,
                width: 100,
            });
        }

        staticColumns.push({
            title: !showProvisionGroups ? t("columns.commission") : t("columns.workspace"),
            dataIndex: "payout_formula",
            key: "payout_formula",
            sorter: true,
            render: (formula: string, record: CommissionData) =>
                formula ? (
                    <InteractiveCell.PayoutFormula
                        type={"input"}
                        defaultValue={{
                            payoutFormula: formula,
                            formulaType: record.formula_type as "formula" | "percent" | "fix",
                            isEnabled: record.is_enabled,
                        }}
                        onSave={(value) => updateParent(value, record)}
                        currencyId={record.offer?.currency_id}
                        isEnabled={record.is_enabled}
                        readOnly={!(editPermission && permissionsUtils.hasPermission(editPermission))}
                    />
                ) : (
                    <EmptyCell />
                ),
            width: 130,
            ellipsis: true,
        });

        if (!firstCommission || !showProvisionGroups) {
            return staticColumns;
        }

        const transformedCommissions = firstCommission.children?.reduce(
            (acc, child) => {
                if (child.commission_group_id) {
                    acc[child.commission_group_id] = child;
                } else {
                    acc["default"] = child;
                }

                return acc;
            },
            {} as Record<string, App.Data.CommissionData>
        );

        const dynamicColumns = Object.entries(transformedCommissions || {})?.map(([groupId, child]) => ({
            title: child.commissionGroup?.title ?? t("columns.default_group"),
            dataIndex: [`children`],
            key: child.commissionGroup?.title ?? t("columns.default_group"),
            render: (children: CommissionData[], record: CommissionData) => {
                // Find child by commission_group_id, not by index
                const matchingChild = children?.find((c) =>
                    groupId === "default" ? !c.commission_group_id : c.commission_group_id === groupId
                );

                return matchingChild ? (
                    <InteractiveCell.PayoutFormula
                        defaultValue={{
                            payoutFormula: matchingChild.payout_formula || "",
                            formulaType: matchingChild.formula_type as "formula" | "percent" | "fix",
                            isEnabled: matchingChild.is_enabled,
                        }}
                        onSave={(value) => updateChild(value, record, matchingChild.id)}
                        currencyId={record.offer?.currency_id}
                        isEnabled={matchingChild.is_enabled}
                        readOnly={!(editPermission && permissionsUtils.hasPermission(editPermission))}
                    />
                ) : (
                    <EmptyCell />
                );
            },
            width: 130,
            ellipsis: true,
        }));

        if (dynamicColumns?.length === 0) {
            return staticColumns;
        }

        return [...staticColumns, ...(dynamicColumns && dynamicColumns.length > 0 ? dynamicColumns : [])];
    }, [fetchCommissionListQuery.data?.commissions]);

    useEffect(() => {
        setKey(columns.map((col) => col.title).join(""));
    }, [columns]);

    if (!columns) {
        return <></>;
    }

    return (
        <CommissionsTable
            key={key}
            editPermission={editPermission}
            columns={columns}
            commissionGroupId={commissionGroupId}
            offer={offer}
            cascade={cascade}
            isLoading={fetchCommissionListQuery.isLoading || fetchCommissionListQuery.isFetching}
            onRefetchTable={(fn) => {
                refetchFromChildRef.current = fn;
            }}
        />
    );
};
